import { memo, useEffect, useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { useDispatch, useSelector } from 'react-redux';
import { teamsWithExtraSyncedDataSelector } from 'state/selectors/teams';
import { StyledTeams } from './Teams.style';
import Table from 'components/base/Table';
import { SPACES } from 'common/constants/sizes';
import {
  TableNoLeftRightBorders,
  WhiteSection
} from 'components/base/layout-components';
import { getTeamsTableCols, getTeamsTableRows } from './teams.service';
import TeamsHeader from './TeamsHeader';
import useTeamsFilters from './useTeamsFilters';
import { TEAM_MODEL } from './teams.model';
import useTeams from './useTeams';
import { contributorsSelector } from 'state/selectors/contributors';
import { accountsSelector } from 'state/selectors/account';
import { getAccounts } from 'state/callers';
import useAccountType from 'views/root/views/home/hooks/useAccountType';
import { filterAllTeamsGroupAndAllContributors } from '../ContributorsAndTeams.service';
import { SESSION_STORAGE_KEYS } from 'common/constants/constants';
import { useTeamsTree } from './useTeamsTree';

const Teams = () => {
  const {
    onConfigureTeam,
    onDeleteTeam,
    onDeleteGroup,
    toggleSyncTeamsModal,
    onConfigureGroup
  } = useTeams();
  const { flatTeams, teamsTree } = useTeamsTree();

  const { searchInput, setSearchInput, teamsInView } = useTeamsFilters({
    teams: flatTeams,
    treeTeams: teamsTree
  });
  const teamsWithExtraData = useSelector(teamsWithExtraSyncedDataSelector);
  const teamsWithExtraDataFiltered = useMemo(
    () => filterAllTeamsGroupAndAllContributors(teamsWithExtraData),
    [teamsWithExtraData]
  );

  const contributors = useSelector(contributorsSelector);
  const dispatch = useDispatch();
  const accounts = useSelector(accountsSelector);
  const { isCurrentUserAdminOrSuperUser } = useAccountType();

  const teamsRows = useMemo(() => {
    const teamsWithExtraDataIds = keyBy(teamsWithExtraDataFiltered, 'id');

    const rows = getTeamsTableRows({
      teams: teamsInView,
      onConfigureTeam,
      onConfigureGroup,
      onDeleteTeam,
      onDeleteGroup,
      contributors,
      teamsWithExtraDataIds,
      isCurrentUserAdminOrSuperUser,
      accounts
    });
    return rows;
  }, [teamsInView]);

  const columns = useMemo(() => getTeamsTableCols());

  useEffect(() => {
    dispatch(getAccounts());
  }, []);

  return (
    <WhiteSection t={SPACES['x-medium']}>
      <StyledTeams>
        <TeamsHeader
          teamsWithExtraData={teamsWithExtraDataFiltered}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onConfigureTeam={onConfigureTeam}
          toggleSyncTeamsModal={toggleSyncTeamsModal}
          onConfigureGroup={onConfigureGroup}
        />
        <TableNoLeftRightBorders>
          <Table
            data={teamsRows}
            columns={columns}
            defaultSortBy={{ key: TEAM_MODEL.CREATED_AT, order: 'desc' }}
            isRowsExpandable={!searchInput}
            persistExpandedRowsProps={{
              storageKey: SESSION_STORAGE_KEYS.TEAMS.TABLE.EXPANDED_ROWS_STATE,
              getRowId: row => row.id
            }}
          />
        </TableNoLeftRightBorders>
      </StyledTeams>
    </WhiteSection>
  );
};

export default memo(Teams);
